import { useCallback, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import type { WatchlistItem, StockAsset, EtfAsset, FundAsset } from "@/schemas";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Target,
  TrendingUp,
  TrendingDown,
  Settings2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AssetLogo } from "@/components/asset/asset-logo";
import { formatPrice } from "@/lib/formatters";
import { resolvePreferredScreenerAssetId } from "@/lib/preferred-asset";
import { useCurrencyMap } from "@/hooks/use-currency-map";
import { KeyMetrics } from "./KeyMetrics";
import { PriceChart } from "./PriceChart";

interface AssetDetailModalProps {
  item: WatchlistItem | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AssetDetailModal({
  item,
  isOpen,
  onClose,
}: AssetDetailModalProps) {
  const router = useRouter();
  const { resolveCurrency } = useCurrencyMap();
  
  // State for period-based data from PriceChart
  const [periodData, setPeriodData] = useState<{
    period: string;
    priceChange: number;
    currentPrice: number | null;
  }>({ period: "1m", priceChange: 0, currentPrice: null });

  const handlePeriodData = useCallback((data: { period: string; priceChange: number; currentPrice: number | null }) => {
    setPeriodData(data);
  }, []);

  if (!item?.asset) {
    return null;
  }

  const asset = item.asset;
  const stockOrEtf =
    asset.type === "stock" || asset.type === "etf" || asset.type === "fund"
      ? (asset as StockAsset | EtfAsset | FundAsset)
      : null;
  const assetCurrency =
    asset.type === "stock" ||
    asset.type === "etf" ||
    asset.type === "fund" ||
    asset.type === "crypto"
      ? asset.currency
      : undefined;

  // Use period-based price change from chart
  const priceChange = periodData.priceChange;
  // Display current price from chart data, fallback to price prop
  const displayPrice = periodData.currentPrice ?? item.latest_price;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="wide" className="max-h-[90vh] p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between pr-8">
            <div className="flex items-center gap-4">
              <AssetLogo asset={asset} size="xl" />
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <DialogTitle
                    className="text-2xl font-black cursor-pointer hover:text-primary hover:underline transition-all"
                    onClick={async () => {
                      const targetAssetId = await resolvePreferredScreenerAssetId(asset);

                      router.navigate({
                        to: "/screener/$assetId",
                        params: { assetId: targetAssetId },
                      });
                      onClose();
                    }}
                  >
                    {asset.symbol}
                  </DialogTitle>
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none px-2 capitalize">
                    {asset.type}
                  </Badge>
                </div>
                <DialogDescription className="text-sm">
                  {asset.name}
                </DialogDescription>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {displayPrice && (
                <div className="text-right">
                  <p className="text-3xl font-mono font-bold tracking-tighter">
                    {formatPrice(
                      displayPrice,
                      resolveCurrency(item.latest_price_currency || assetCurrency)
                    )}
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <span
                      className={cn(
                        "text-sm font-bold flex items-center gap-0.5",
                        priceChange >= 0 ? "text-green-500" : "text-red-500"
                      )}
                    >
                      {priceChange >= 0 ? (
                        <TrendingUp className="h-4 w-4" />
                      ) : (
                        <TrendingDown className="h-4 w-4" />
                      )}
                      {priceChange >= 0 ? "+" : ""}
                      {priceChange.toFixed(2)}%
                      <span className="text-xs text-muted-foreground ml-1">
                        ({periodData.period.toUpperCase()})
                      </span>
                    </span>
                    {item.target_price && (
                      <p className="text-xs font-medium text-amber-600 flex items-center gap-1">
                        <Target className="h-3.5 w-3.5" />
                        Target: {item.target_price}
                      </p>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <ScrollArea className="flex-1 max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {/* Chart + Key Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <PriceChart
                  assetId={item.asset_id}
                  currency={item.latest_price_currency || assetCurrency || "USD"}
                  previousClose={stockOrEtf?.previous_close}
                  previousCloseAt={stockOrEtf?.previous_close_at}
                  formatPrice={formatPrice}
                  resolveCurrency={resolveCurrency}
                  onPeriodData={handlePeriodData}
                />
              </div>
              <div className="lg:col-span-1">
                <KeyMetrics asset={asset} />
              </div>
            </div>

            {/* Notes */}
            {item.note && (
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                <div className="flex items-center gap-2 mb-2">
                  <Settings2 className="h-4 w-4 text-primary" />
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">
                    My Notes
                  </span>
                </div>
                <p className="text-sm leading-relaxed">{item.note}</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
